import { useState, useEffect } from "react";
import { Loader2, Send, Link2, Unlink } from "lucide-react";
import { colors, fonts } from "../../styles/theme";
import {
  getLinkedInStatus,
  connectLinkedIn,
  disconnectLinkedIn,
} from "../../services/api";

export default function LinkedInConnectionSection() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  useEffect(() => {
    getLinkedInStatus()
      .then((s) => setStatus(s))
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("linkedin") === "connected") {
      setSuccess("LinkedIn connected successfully!");
      setTimeout(() => setSuccess(null), 5000);
      window.history.replaceState({}, "", window.location.pathname);
      getLinkedInStatus().then(setStatus).catch(() => {});
    }
  }, []);

  const handleConnect = async () => {
    setActionLoading(true);
    setError(null);
    try {
      const { authUrl } = await connectLinkedIn();
      window.location.href = authUrl;
    } catch (e) {
      setError(e.message);
      setActionLoading(false);
    }
  };

  const handleDisconnect = async () => {
    if (!window.confirm("Disconnect LinkedIn? You will no longer be able to post.")) return;
    setActionLoading(true);
    setError(null);
    try {
      await disconnectLinkedIn();
      setStatus({ connected: false });
      setSuccess("LinkedIn disconnected");
      setTimeout(() => setSuccess(null), 3000);
    } catch (e) {
      setError(e.message);
    } finally {
      setActionLoading(false);
    }
  };

  return (
    <div
      style={{
        background: colors.bgCard,
        border: `1px solid ${colors.border}`,
        borderRadius: 8,
        padding: 24,
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 10,
          marginBottom: 16,
        }}
      >
        <Send size={18} color="#0077b5" />
        <h3
          style={{
            fontFamily: fonts.heading,
            fontSize: 16,
            color: colors.text,
            margin: 0,
          }}
        >
          LinkedIn
        </h3>
        {status?.connected && (
          <span
            style={{
              fontSize: 11,
              padding: "2px 8px",
              borderRadius: 10,
              background: `${colors.success}18`,
              color: colors.success,
              fontWeight: 600,
            }}
          >
            Connected
          </span>
        )}
      </div>

      {error && (
        <div
          style={{
            padding: "8px 12px",
            marginBottom: 12,
            borderRadius: 6,
            background: `${colors.danger}15`,
            color: colors.danger,
            fontSize: 12,
          }}
        >
          {error}
        </div>
      )}
      {success && (
        <div
          style={{
            padding: "8px 12px",
            marginBottom: 12,
            borderRadius: 6,
            background: `${colors.success}15`,
            color: colors.success,
            fontSize: 12,
          }}
        >
          {success}
        </div>
      )}

      {loading ? (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            color: colors.textSecondary,
            fontSize: 13,
          }}
        >
          <Loader2 size={14} className="spin" />{" "}
          Checking connection…
        </div>
      ) : status?.connected ? (
        <div>
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: 8,
              marginBottom: 16,
            }}
          >
            {status.personName && (
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  fontSize: 13,
                }}
              >
                <span style={{ color: colors.textSecondary }}>Profile</span>
                <span style={{ color: colors.text }}>{status.personName}</span>
              </div>
            )}
            {status.organizationName && (
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  fontSize: 13,
                }}
              >
                <span style={{ color: colors.textSecondary }}>Company Page</span>
                <span style={{ color: colors.text }}>{status.organizationName}</span>
              </div>
            )}
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                fontSize: 13,
              }}
            >
              <span style={{ color: colors.textSecondary }}>Token Expires</span>
              <span style={{ color: colors.text }}>
                {status.expiresAt
                  ? new Date(status.expiresAt).toLocaleDateString("en-GB", {
                      day: "numeric",
                      month: "short",
                      year: "numeric",
                    })
                  : "—"}
              </span>
            </div>
          </div>
          <button
            onClick={handleDisconnect}
            disabled={actionLoading}
            style={{
              padding: "8px 16px",
              fontSize: 13,
              fontWeight: 600,
              fontFamily: fonts.body,
              background: "transparent",
              color: colors.danger,
              border: `1px solid ${colors.danger}`,
              borderRadius: 6,
              cursor: "pointer",
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
            }}
          >
            {actionLoading ? (
              <Loader2 size={14} className="spin" />
            ) : (
              <Unlink size={14} />
            )}
            Disconnect
          </button>
        </div>
      ) : (
        <div>
          <p
            style={{
              fontSize: 13,
              color: colors.textSecondary,
              marginBottom: 12,
            }}
          >
            Connect your LinkedIn account to post updates to your personal profile and company page directly from Insights.
          </p>
          <button
            onClick={handleConnect}
            disabled={actionLoading}
            style={{
              padding: "8px 18px",
              fontSize: 13,
              fontWeight: 600,
              fontFamily: fonts.body,
              background: "#0077b5",
              color: "#ffffff",
              border: "none",
              borderRadius: 6,
              cursor: "pointer",
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
            }}
          >
            {actionLoading ? (
              <Loader2 size={14} className="spin" />
            ) : (
              <Link2 size={14} />
            )}
            Connect to LinkedIn
          </button>
        </div>
      )}
    </div>
  );
}
